/**
 * ``/workspace/templates/edit/$slug`` — single-template editor route.
 *
 * Renders the v1-style ``<TemplateEditor>`` (collapsible Chrome/Paper/Voice
 * blocks + JSON escape hatch + sticky 4:3 preview) for one custom or
 * built-in template addressed by slug.
 *
 * Why a sibling route to ``$kind.tsx`` rather than overloading it:
 *   - ``$kind`` is the kind-driven split editor the StudioDrawer drills
 *     into (URL: ``/workspace/templates/chrome``). It's the deep editing
 *     surface where the user already knows the kind they want to tweak.
 *   - ``edit/$slug`` is the v1 single-template surface reached from the
 *     grid (``/workspace/templates``). It carries the broader "browse +
 *     edit one specific template" flow.
 *
 * Both routes coexist under the same ``route.tsx`` shell so the chrome
 * (header + tab strip) stays uniform across surfaces.
 */

import * as React from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { SurfaceEmptyState, Button } from "@ando/ui";

import {
  TemplateEditor,
  useTemplateDetail,
  useUpdateTemplate,
  useDeleteTemplate,
} from "@/features/templates";

export const Route = createFileRoute("/_authed/workspace/templates/edit/$slug")({
  component: TemplateEditorRoute,
});

function TemplateEditorRoute() {
  const { i18n } = useLingui();
  const { slug } = useParams({ from: "/_authed/workspace/templates/edit/$slug" });
  const navigate = useNavigate();
  const detailQuery = useTemplateDetail(slug);
  const updateTpl = useUpdateTemplate();
  const deleteTpl = useDeleteTemplate();

  const goBack = React.useCallback(() => {
    void navigate({ to: "/workspace/templates" });
  }, [navigate]);

  if (detailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center px-8 py-16 text-ink-3 text-sm">
        {i18n._(msg`Caricamento template…`)}
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="px-8 py-16">
        <SurfaceEmptyState
          title={i18n._(msg`Template non trovato`)}
          description={i18n._(
            msg`Il template richiesto non esiste oppure non hai i permessi per visualizzarlo.`,
          )}
          action={
            <Button size="sm" onClick={goBack}>
              {i18n._(msg`Torna alla lista`)}
            </Button>
          }
        />
      </div>
    );
  }

  const detail = detailQuery.data;
  const readOnly = detail.scope === "builtin";

  return (
    <TemplateEditor
      detail={detail}
      readOnly={readOnly}
      isSaving={updateTpl.isPending}
      onBack={goBack}
      onSave={async ({ name, description, spec }) => {
        if (!detail.slug) return;
        await updateTpl.mutateAsync({
          slug: detail.slug,
          body: { name, description, spec },
        });
      }}
      onDelete={
        detail.slug && detail.scope !== "builtin"
          ? async () => {
              if (!detail.slug) return;
              await deleteTpl.mutateAsync(detail.slug);
              goBack();
            }
          : undefined
      }
    />
  );
}
